<template>
	<div id="tour-finder">
		<transition name="banner-fade" mode="out-in">
			<banner
				:content="getBannerContent"
				v-show="bannerVisable"
				@closeBanner="closeHomeBanner"
				key="inner-banner-home"
				layout="small"
				class="home-banner"
			/>
		</transition>
		<workflow-finder
			:contextId="contextId"
			v-bind="workflowFinderParams"
			@selection="updateRouteQuery($event)"
			@redirect="goToWorkflow($event)"
			@clear-selection="clearSelection()"
		></workflow-finder>
		<recommended-workflows contextId="wfc-main-app"></recommended-workflows>
    	<QIAagentBanner />
		<workflow-disclaimer />
	</div>
</template>

<script>
import {mapGetters} from 'vuex';
import workflowDisclaimer from '@/shared/workflowDisclaimer.vue';
import {trackEvent, trackObject, createObject} from '@/services/tracking';
import RecommendedWorkflows from '@/components/workflow/RecommendedWorkflows.vue';
import WorkflowFinder from '@/components/workflow/WorkflowFinder.vue';
import QIAagentBanner from "@/shared/QIAagentBanner.vue";

export default {
	name: 'Home',
	components: {
    QIAagentBanner, WorkflowFinder, workflowDisclaimer, RecommendedWorkflows},
	props: {
		application: {
			default: '',
			type: String,
		},
		startingMaterial: {
			default: '',
			type: String,
		},
		analyte: {
			default: '',
			type: String,
		},
		technology: {
			default: '',
			type: String,
		},
	},
	data() {
		return {
			contextId: 'wfc-main-app',
			bannerVisable: false,
			banner: {
				title: '',
				body: '',
				version: '',
				btn: '',
				btnEmits: '',
			},
		};
	},
	computed: {
		...mapGetters({getInitialValuesByContextId: 'WorkflowFinder/getInitialValuesByContextId'}),
		language() {
			return this.$store.state.LANG;
		},
		getBannerContent() {
			return this.banner;
		},
		workflowFinderParams() {
			const init = this.getInitialValuesByContextId(this.contextId);
			return {
				application: this.application || init?.application,
				startingMaterial: this.startingMaterial || init?.startingMaterial,
				analyte: this.analyte || init?.analyte,
				technology: this.technology || init?.technology,
			};
		},
	},
	async created() {
		this.$root.$emit('closeGlobalBanner');
		const isLoggedIn = this.getLoggedInStatus();
		const s_dtm = createObject('workflow finder', this.getCountry(), isLoggedIn, this.getUserId());
		trackObject(s_dtm);

		this.$store.dispatch('WorkflowFinder/fetchFinderConfiguration');
	},
	async mounted() {
		await this.resetWFCStoredData();
		await this.$nextTick();
		if (this.$store.state.noWorkflowFound) {
			this.showHomeBannerInit(this.$store.state.noWorkflowFound);
			this.$store.commit('setNoWorkflowFound', false);
		}
	},
	methods: {
		showHomeBanner(obj) {
			this.banner = obj;
			this.bannerVisable = true;
		},
		closeHomeBanner() {
			this.bannerVisable = false;
		},
		showHomeBannerInit(version = 'noWorkflowsFound', time = 6000) {
			let obj;
			switch (version) {
				case 'emptyWorkflow':
					obj = {
						title: 'The experiment you are looking for doesn’t exist or contains products that are not available in your country.',
						body: 'Search for alternative experiments below.',
						version: 'warning',
					};
					break;
				case 'snapshotExistsError':
					obj = {
						title: 'The experiment you are looking for doesn’t exist or has been removed.',
						body: 'Ask the experiment owner to verify the link or search below for alternative experiments below.',
						version: 'warning',
					};
					break;
				case 'snapshotDisplayError':
					obj = {
						title: 'The experiment you are looking for could not be displayed.',
						body: 'Due to technical reasons, we were unable to display the experiment. Please try again in a few minutes.',
						version: 'error',
					};
					break;
				default:
					obj = {
						title: 'The experiment you are looking for doesn’t exist or contains products that are not available in your country.',
						body: 'Search for alternative experiments below.',
						version: 'warning',
					};
			}

			this.showHomeBanner(obj);
			if (!obj?.dismissible) setTimeout(() => this.closeHomeBanner(), time);
		},
		updateRouteQuery({selection, category}) {
			const selectionValue = selection.key;
			return this.$router.replace({
				name: 'Home',
				query: {...this.$route.query, [category]: selectionValue},
			});
		},
		goToWorkflow(params) {
			this.$router.push({
				name: 'Workflow',
				params,
			});
		},
		clearSelection() {
			this.$router.replace({name: 'Home', query: {}}).catch((err) => err);
		},
		// returns true if options is disabled. Adds 'disabled' to dropdown options
		optDisabled(cvlId, key) {
			return !this.isDisabled(cvlId, key).some((arr) => arr.includes(key));
		},
		// returns true if product is valid ie: visibility for country, isn't phased out... Options wont show in dropdown if false
		async resetWFCStoredData() {
			const tourKeys = ['product', 'finder', 'workflow', 'eln'].map(t => `tour-${t}`);
			const tourValues = tourKeys.map(k => [k, localStorage.getItem(k)]);
			localStorage.clear();
			tourValues.forEach(([k, v]) => { if (v !== null) localStorage.setItem(k, v); });
			localStorage.setItem(
				'registrationOriginApplication',
				JSON.stringify({value: 'WFC', timestamp: Date.now()}),
			);
			await this.$store.commit('setWorkflowHeadline', null);
			await this.$store.commit('setWorkflowDefaultHeadline', null);
			await this.$store.commit('setWorkFlowId', null);
			await this.$store.commit('setMyWorkflow', null);
			await this.$store.commit('setMyWorkflows', null);
			await this.$store.commit('setLastModDate', null);
		},
		getLoggedInStatus() {
			const jwt = this.$store.state.jwt;
			if (jwt) return `${jwt.lastName}, ${jwt.firstName}`;
			return null;
		},
		getCountry() {
			return this.$store.state.jwt ? this.$store.state.jwt.country : this.$store.state.COUNTRY;
		},
		getUserId() {
			const jwt = this.$store.state.jwt;
			if (jwt) return jwt.sub;
			return 'not loggedin';
		},
	},
};
</script>

<style lang="scss" scoped>
.md-snackbar.md-position-left {
	@media (max-width: $mobile) {
		bottom: 76px;
		left: 10px;
	}
}

.home-banner {
	margin-top: 40px;
}

.workflows {
	flex: 1;
	width: 100%;
	display: flex;
	flex-direction: column;
	align-items: center;
	color: $color-grey;
}
</style>
